import { useAppBootstrapV2, type SubscribeAndSaveOffers } from '@brandclub/common-ui';
import { queryOptions, useQueries, useQuery } from '@tanstack/react-query';

import { getSubscribeAndSaveOffer } from '../rest/checkout';
import { HOST_DOMAIN } from '../../utils/domain';
import { CachedQueryKeys } from './cachedQueryKeys';

/** One request per product. `null` is a product with no offer. */
export const subscribeAndSaveOfferQueryOptions = (stacklineSku: string) =>
  queryOptions({
    queryKey: [CachedQueryKeys.SubscribeAndSaveOffer, stacklineSku],
    queryFn: ({ signal }) => getSubscribeAndSaveOffer(stacklineSku, signal),
    staleTime: 10 * 60 * 1000,
    retry: false,
  });

/** The offer of the product page. The app asks for it only while its Subscribe & Save enrollment is open. */
export const useProductSubscribeAndSaveOffer = (stacklineSku: string | undefined) => {
  const { data: enrollmentOpen } = useAppBootstrapV2(HOST_DOMAIN, {
    select: (bootstrap) => bootstrap.capabilities?.subscribeAndSave?.enrollmentOpen === true,
  });
  return useQuery({
    ...subscribeAndSaveOfferQueryOptions(stacklineSku ?? ''),
    enabled: !!stacklineSku && enrollmentOpen === true,
  });
};

/** The offers of the given products, by stackline sku. A product with no offer, or one not yet read, has none. */
export const useSubscribeAndSaveOffers = (stacklineSkus: string[]): SubscribeAndSaveOffers =>
  useQueries({
    queries: stacklineSkus.map(subscribeAndSaveOfferQueryOptions),
    combine: (results) =>
      Object.fromEntries(stacklineSkus.map((sku, index) => [sku, results[index]?.data ?? undefined])),
  });
